import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, refreshSession } from "@/api/client";
import type { components } from "@/api/schema";
import { identificarUsuario, limparUsuario } from "@/lib/sentry";
import {
  getAccessToken,
  setAccessToken,
  setImpersonatedBy,
  setSessionLostHandler,
} from "./tokens";

export type User = components["schemas"]["User"];

export interface SignupInput {
  name: string;
  email: string;
  password: string;
  establishmentName: string;
  timezone: string;
  /** Segmento do negócio — decide o catálogo de personas do bot. */
  vertical: string;
}
/**
 * `offline` é o estado que faltava: não sabemos se há sessão porque o
 * servidor não respondeu. Diferente de `anonymous`, que é o servidor tendo
 * dito que a credencial não vale — misturar os dois deslogava o dono
 * sempre que a API estivesse fora do ar.
 */
export type SessionStatus = "loading" | "authenticated" | "anonymous" | "offline";

export interface AuthContextValue {
  status: SessionStatus;
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (input: SignupInput) => Promise<void>;
  loginWithGoogle: (
    idToken: string,
    account?: { establishmentName: string; timezone: string; vertical: string },
  ) => Promise<void>;
  logout: () => Promise<void>;
  /** Refaz a resolução da sessão — o botão da tela de "servidor fora". */
  retrySession: () => void;
  /**
   * Substitui o usuário da sessão por um já devolvido pela API (hoje só o
   * `PUT /auth/me` da tela de conta). O `user` daqui é a fonte do nome e do
   * e-mail no cabeçalho — sem isto, trocar o nome gravava no banco e a tela
   * continuava exibindo o antigo até o próximo F5.
   */
  applyUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Chamadas que falham na rede (offline, servidor fora, CORS bloqueando a
 * resposta) lançam antes de virar `{ error }`. Sem isto elas cairiam na
 * mensagem genérica de credencial, que mente sobre a causa.
 */
async function withNetworkError<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (cause) {
    if (cause instanceof AuthError) throw cause;
    throw new AuthError("network_error", "Sem conexão com o servidor.");
  }
}

/** Erro de auth que já carrega o `code` estável da API. */
export class AuthError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "AuthError";
    this.code = code;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [user, setUser] = useState<User | null>(null);

  // Marca o dono no escopo do Sentry sempre que a sessão muda. Fica num
  // efeito, e não espalhado pelos cinco pontos que chamam setUser
  // (login, signup, refresh, applyUser, impersonation): um deles
  // esquecido produziria erro atribuído à pessoa errada, que é pior do
  // que erro sem atribuição nenhuma.
  //
  // Só o UUID e o estabelecimento — nunca nome ou e-mail, o mesmo
  // compromisso do `sendDefaultPii: false` em lib/sentry.ts.
  useEffect(() => {
    if (user) {
      identificarUsuario(user.id, user.establishment_id);
    } else {
      limparUsuario();
    }
  }, [user]);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    // Sem esta linha a marca de sessão de suporte sobrevive ao logout: ela
    // mora em módulo, não em estado do React, então a faixa continuaria
    // no topo dizendo que alguém está vendo a conta depois de a sessão já
    // ter acabado.
    setImpersonatedBy(null);
    setUser(null);
    setStatus("anonymous");
  }, []);

  // Reidratação no reload: o access token morreu com a página, mas o cookie
  // httpOnly de refresh sobreviveu. Um 401 aqui é o caminho normal de quem
  // nunca logou — não é erro. Já a AUSÊNCIA de resposta não diz nada sobre a
  // sessão, e é por isso que os dois casos são tratados separados.
  const resolveSession = useCallback(
    async (cancelled: () => boolean) => {
      if (!getAccessToken()) {
        const outcome = await refreshSession();
        if (cancelled()) return;
        if (outcome.status === "unreachable") {
          // Não limpa nada: o cookie continua no navegador e a sessão pode
          // muito bem estar viva do outro lado.
          setStatus("offline");
          return;
        }
        if (outcome.status === "no_session") {
          clearSession();
          return;
        }
      }

      try {
        const { data, error } = await api.GET("/auth/me");
        if (cancelled()) return;
        if (error || !data?.data) {
          clearSession();
          return;
        }
        setUser(data.data);
        setStatus("authenticated");
      } catch {
        // openapi-fetch LANÇA quando a resposta não chega (mesma razão do
        // withNetworkError acima); `{ error }` é o servidor respondendo.
        if (!cancelled()) setStatus("offline");
      }
    },
    [clearSession],
  );

  useEffect(() => {
    let cancelled = false;
    void resolveSession(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [resolveSession]);

  // O botão da tela de "servidor fora" refaz exatamente o mesmo caminho do
  // boot. Sem cancelamento: é um clique deliberado e o provider vive tanto
  // quanto o app.
  const retrySession = useCallback(() => {
    setStatus("loading");
    void resolveSession(() => false);
  }, [resolveSession]);

  // O middleware do cliente avisa aqui quando o refresh falha de vez.
  useEffect(() => {
    setSessionLostHandler(clearSession);
    return () => setSessionLostHandler(() => {});
  }, [clearSession]);

  const login = useCallback(async (email: string, password: string) => {
    return withNetworkError(async () => {
      const { data, error } = await api.POST("/auth/login", {
        body: { email, password },
      });
      if (error || !data) {
        throw new AuthError(
          error?.error.code ?? "unknown_error",
          error?.error.message ?? "Não foi possível entrar.",
        );
      }
      setAccessToken(data.data.access_token);
      setUser(data.data.user);
      setStatus("authenticated");
    });
  }, []);

  const signup = useCallback(async (input: SignupInput) => {
    return withNetworkError(async () => {
      const { data, error } = await api.POST("/auth/signup", {
        body: {
          name: input.name,
          email: input.email,
          password: input.password,
          establishment_name: input.establishmentName,
          timezone: input.timezone,
          vertical: input.vertical,
        },
      });
      if (error || !data) {
        throw new AuthError(
          error?.error.code ?? "unknown_error",
          error?.error.message ?? "Não foi possível criar a conta.",
        );
      }
      setAccessToken(data.data.access_token);
      setUser(data.data.user);
      setStatus("authenticated");
    });
  }, []);

  const loginWithGoogle = useCallback(
    async (
      idToken: string,
      account?: { establishmentName: string; timezone: string; vertical: string },
    ) => {
      // O spec manda tentar sem os campos de estabelecimento primeiro; se a
      // conta ainda não existe a API responde 422 establishment_required e aí
      // sim reenviamos preenchido.
      return withNetworkError(async () => {
        const body = account
          ? {
              id_token: idToken,
              establishment_name: account.establishmentName,
              timezone: account.timezone,
              vertical: account.vertical,
            }
          : { id_token: idToken };

        const { data, error } = await api.POST("/auth/google", { body });
        if (error || !data) {
          throw new AuthError(
            error?.error.code ?? "unknown_error",
            error?.error.message ?? "Não foi possível entrar com o Google.",
          );
        }
        setAccessToken(data.data.access_token);
        setUser(data.data.user);
        setStatus("authenticated");
      });
    },
    [],
  );

  const applyUser = useCallback((updated: User) => setUser(updated), []);

  const logout = useCallback(async () => {
    // Idempotente do lado do backend: sem cookie ele responde 204 igual.
    await api.POST("/auth/logout");
    clearSession();
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      isAuthenticated: status === "authenticated",
      login,
      signup,
      loginWithGoogle,
      logout,
      retrySession,
      applyUser,
    }),
    [status, user, login, signup, loginWithGoogle, logout, retrySession, applyUser],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error("useAuth precisa estar dentro de <AuthProvider>");
  return context;
}
